import React, { useEffect } from 'react';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { I18n } from 'aws-amplify/utils';
import { translations } from '@aws-amplify/ui-react';
import "../../styles.css";


// Load Amplify's built-in dictionaries, switch to French, then override the
// few labels we care about so the whole authenticator renders in French.
I18n.putVocabularies(translations);
I18n.setLanguage('fr');
I18n.putVocabularies({
  fr: {
    'Sign in': 'Se connecter',
    'Signing in': 'Connexion en cours',
    'Sign In': 'Se connecter',
    'Forgot your password?': 'Mot de passe oublié ?',
    'Reset Password': 'Réinitialiser le mot de passe',
    'Reset your password': 'Réinitialiser votre mot de passe',
    'Enter your email': 'Saisissez votre adresse e-mail',
    'Send code': 'Envoyer le code',
    'Back to Sign In': 'Retour à la connexion',
    'Confirm': 'Confirmer',
    'Submit': 'Valider',
    'Code': 'Code',
    'New Password': 'Nouveau mot de passe',
    'Enter your Password': 'Saisissez votre mot de passe',
    'Please confirm your Password': 'Confirmez votre mot de passe',
    'Incorrect username or password.':
      'Adresse e-mail ou mot de passe incorrect.',
    'User does not exist.': 'Cet utilisateur n’existe pas.',
    'Password attempts exceeded':
      'Nombre de tentatives dépassé, réessayez plus tard.',
  },
});

const formFields = {
  signIn: {
    username: {
      labelHidden: true,
      placeholder: 'Adresse e-mail',
      required: true,
      order: 1,
    },
    password: {
      labelHidden: true,
      placeholder: 'Mot de passe',
      required: true,
      order: 2,
    },
  },
};

const setCookie = (name, value, days) => {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
};

// Only allow internal relative paths as a post-login destination, never an
// absolute URL or protocol-relative one (open-redirect guard).
const safeReturnPath = (raw) => {
  if (!raw) return null;
  let value;
  try {
    value = decodeURIComponent(raw);
  } catch {
    return null;
  }
  if (!value.startsWith('/') || value.startsWith('//')) {
    return null;
  }
  return value;
};

const docBaseUrl = () => {
  const environment = process.env.REACT_APP_ENVIRONMENT;
  if (environment === 'production') {
    return 'https://documentation.g8.gestimag.com';
  }
  return `https://documentation.${environment}.gestimag.com`;
};

// Final destination after sign-in: honour ?returnTo=<internal path> when the
// Lambda@Edge forwarded the originally requested deep link, else the home page.
const targetUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const returnTo = safeReturnPath(params.get('returnTo'));
  return returnTo ? `${docBaseUrl()}${returnTo}` : docBaseUrl();
};

// Rendered once the user is authenticated: persists the token cookie then
// performs a full-page redirect. Runs in an effect (never during render) so
// nothing of the Amplify form stays mounted — only this clean loader shows.
function Redirecting() {
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session?.tokens?.accessToken?.toString();
        if (token) {
          setCookie('authToken', token, 7);
        }
        if (!cancelled) {
          window.location.href = targetUrl();
        }
      } catch (error) {
        console.error('Erreur lors de la redirection:', error);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="redirect-screen">
      <div className="redirect-spinner" aria-hidden="true" />
      <p className="redirect-text">Connexion réussie, redirection…</p>
    </div>
  );
}

// Reads the machine state via the hook so, the instant auth succeeds, the
// WHOLE Authenticator is unmounted and replaced by the loader — no leftover
// empty Amplify container (the white block) remains on screen.
function AuthGate() {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);

  if (authStatus === 'authenticated') {
    return <Redirecting />;
  }

  return (
    <Authenticator
      formFields={formFields}
      hideSignUp={true}
      className="authenticator"
    />
  );
}

function Authentication() {
  return (
    <div style={{ width: '100%' }}>
      <Authenticator.Provider>
        <AuthGate />
      </Authenticator.Provider>
    </div>
  );
}

export default Authentication;
